/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiTypes, uiSelectors, layerSelectors } from '@opengeoweb/store';
import DatasetList from './DatasetList';
import { dataExplorerSelectors } from '../../store';
import type { DataExplorerModuleState } from '../../store/types';
import { useFetchItems } from '../../utils/dataexplorer-api/hooks';

export const DatasetListConnect: React.FC = () => {
  const activeMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.DataExplorer),
  );

  const baseUrl = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getDiscoveryCatalogueURL(store),
  );

  const searchFilter = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getSearchFilter(store),
  );

  const queryables = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getQueryables(store),
  );

  const concepts = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getConcepts(store),
  );

  const mapLayers = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayersByMapId(store, activeMapId),
  );

  const bbox = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getBbox(store),
  );

  const meteogateOnly = useSelector((store: DataExplorerModuleState) =>
    dataExplorerSelectors.getMeteogateOnly(store),
  );

  const { data: datasets } = useFetchItems(
    baseUrl,
    searchFilter,
    queryables,
    concepts,
    bbox,
    meteogateOnly,
  );

  return React.useMemo(() => {
    return (
      <DatasetList
        datasets={datasets}
        mapLayers={mapLayers}
        searchFilter={searchFilter}
      />
    );
  }, [datasets, mapLayers, searchFilter]);
};
